import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Box, CircularProgress, Divider, Grid} from "@mui/material";
import {ProductivityTracker} from "../molecules/ProductivityTracker";
import WorkItemTable, { ViewType, caseContext } from "../organisms/workItemsTable";
import {ProductivityNumbers} from "../../queries/productivityTracker";
import {SnackbarContext} from "../../context/AlertsContext";
import {policiesContext, serviceLineMapper} from "../../utils/common";
import {fetchResolvedConfigurationResult} from "../../utils/api";
import {
    DEFAULT_NAV_VIEW_SET_CONFIG,
    MY_TEAMS_WORK_NAV_VIEW_SET_ID,
    buildNavSelectionStorageKey,
    readPersistedNavSelection,
} from "../organisms/workItemsTable/constants";
import theme from "../../theme/Theme";
import DefaultWrapper from "./DefaultWrapper";
import {WorkItemSettingsBtn} from "../atoms/buttons/WorkItemSettingsBtn";
import QueueSettingsDrawer from "../molecules/QueueSettingsDrawer/QueueSettingsDrawer";
import QueueSettingsModal from "../molecules/QueueSettingsModal/QueueSettingsModal";
import ManagerActionButtons from "../molecules/ManagerActionButtons/ManagerActionButtons";
import NavigationColumn from "../molecules/NavigationColumn";
import { AddViewPopup } from "../molecules/AddViewPopup";

// Bounded retry for the nav-visibility config read; see the effect below for why an
// indeterminate read must not be treated as "feature off".
const NAV_CONFIG_MAX_ATTEMPTS = 3;
const NAV_CONFIG_RETRY_DELAY_MS = 1000;

interface IProps {
    client: any;
    widgetOptions: any;
    directoryType: string;
}

interface IProductivityTrackerProps {
    productivityNumbers: any;
    loading: boolean;
}

const ProductivityTrackerHeader = (props: IProductivityTrackerProps) => {
    const {productivityNumbers, loading} = props;

    return (
        <Grid item container xs={12}>
            {loading ? (
                <Grid container style={{gap: "1em", marginBottom: "20px"}}>
                    <Grid item style={{flex: 1}}>
                        <Box
                            sx={{
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "white",
                                boxShadow: "3",
                                border: "1",
                                borderRadius: "4px",
                            }}
                        >
                            <CircularProgress color="primary" size={20}/>
                        </Box>
                    </Grid>

                    <Grid item>
                        <Divider
                            orientation="vertical"
                            style={{
                                width: "2px",
                                backgroundColor: theme.palette.artisticStroke200.main
                            }}
                        />
                    </Grid>

                    <Grid item style={{flex: 1}}>
                        <Box
                            sx={{
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "white",
                                boxShadow: "3",
                                border: "1",
                                borderRadius: "4px",
                            }}
                        >
                            <CircularProgress color="primary" size={20}/>
                        </Box>
                    </Grid>
                </Grid>
            ) : (
                <ProductivityTracker
                    productivityNumbers={productivityNumbers}
                />
            )}
        </Grid>
    );
}

export default function Worklist(props: IProps) {
    const { client, widgetOptions, directoryType } = props;

    const {openSnackbar} = useContext(SnackbarContext);
    const { userContext, roles, policies } = React.useContext(policiesContext);

    const [showNewNav, setShowNewNav] = useState(false);
    const [productivityNumbers, setProductivityNumbers] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [refresh, setRefresh] = React.useState(false);

    const [queueSettingsOpen, setQueueSettingsOpen] = React.useState(false);
    const [manageQueuesOpen, setManageQueuesOpen] = React.useState(false);

    const hasWorklistSettingsAccess = useMemo(() => {
        // @ts-expect-error activeContext is not typed on userContext
        const resources = userContext?.activeContext?.resources || [];
        return resources.some((resource: any) => resource.resourceName === "worklist_settings");
    }, [userContext]);

    const [views, setViews] = useState<ViewType[]>([]);
    const [activeView, setActiveView] = useState<ViewType | undefined>();
    const [manageViewsAnchorEl, setManageViewsAnchorEl] = useState<HTMLElement | null>(null);
    const [activeNavViewSetId, setActiveNavViewSetId] = useState<string | null>(null);
    // State, not a ref: the table reports `null` for a saved view, which is a no-op on an
    // already-null activeNavViewSetId, so a ref would retire the seed with no re-render.
    const [navSelectionReported, setNavSelectionReported] = useState(false);

    const reportNavViewSet = (id: string | null) => {
        setNavSelectionReported(true);
        setActiveNavViewSetId(id);
    };
    // Use the external ref if provided, otherwise create a local one
    const tableRef = widgetOptions?.childRef || useRef<any>(null);

    useEffect(() => {
        const getProdTrackerData = async () => {
            setLoading(true);

            // Get the current date
            const currentDate = new Date();
            // Set the time to midnight
            currentDate.setHours(0, 0, 0, 0);
            // Convert to ISO string
            const midnightISOString = currentDate.toISOString();
            const variables = {
                currentDate: midnightISOString,
                service: serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name
            };
            await props?.client
                .query({
                    query: ProductivityNumbers,
                    variables: variables,
                    fetchPolicy: "no-cache",
                })
                .then((response: any) => {
                    const productivityNumbers = response.data.productivityNumbers;
                    setProductivityNumbers(productivityNumbers);
                })
                .catch((error: any) => {
                    openSnackbar("Failed to fetch Productivity Tracker statistics", "error");
                });

            setLoading(false);
        }

        if (directoryType) getProdTrackerData();
    }, [refresh, directoryType, client, openSnackbar, userContext]);

    useEffect(() => {
        // Fetch vertical-view-selector configuration.
        //
        // This read is the only gate on whether the left nav renders, and it runs once per
        // widget mount (the widget receives a frozen userContext snapshot), so there is no
        // later pass to correct a bad answer. Collapsing a failed read into "feature off"
        // therefore hid the nav for the entire session on a single transient failure — e.g.
        // the 401 that follows a logout/login cycle while a revoked bearer token is still
        // cached. Retry while the answer is indeterminate; honor an explicit value at once.
        let cancelled = false;

        const delay = (ms: number) =>
            new Promise<void>((resolve) => setTimeout(resolve, ms));

        // Reported only from the two terminal branches below: announcing "still fetching"
        // would flash the host's nav entry back for the length of this request. `null` means
        // never determined, not "off".
        const reportVerticalNav = (isEnabled: boolean | null) =>
            widgetOptions?.onVerticalNavResolved?.({
                tenantId: userContext?.activeContext?.tenantId,
                serviceLine: userContext?.serviceLine?.name,
                isEnabled,
            });

        const fetchNavConfig = async () => {
            if (!userContext?.activeContext?.tenantId || !userContext?.serviceLine?.name) return;

            for (let attempt = 1; attempt <= NAV_CONFIG_MAX_ATTEMPTS; attempt++) {
                let result: Awaited<ReturnType<typeof fetchResolvedConfigurationResult>> | undefined;
                try {
                    result = await fetchResolvedConfigurationResult(
                        "vertical-view-selector",
                        userContext.activeContext.tenantId,
                        userContext.serviceLine.name
                    );
                } catch (error) {
                    console.error("Failed to fetch vertical-view-selector config:", error);
                }

                // Ignore a superseded request (unmounted, or the context changed) so its
                // result can't overwrite the current one.
                if (cancelled) return;

                // Anything that isn't a well-formed resolved result counts as indeterminate.
                if (result?.status === "resolved") {
                    setShowNewNav(result.value === "true");
                    reportVerticalNav(result.value === "true");
                    return;
                }

                if (attempt < NAV_CONFIG_MAX_ATTEMPTS) {
                    await delay(NAV_CONFIG_RETRY_DELAY_MS);
                    if (cancelled) return;
                }
            }

            // Never determined. Leave showNewNav untouched rather than forcing it off, so a
            // failed read cannot tear down a nav column that is already rendered.
            console.warn(
                "vertical-view-selector config unavailable after retries; leaving navigation visibility unchanged"
            );
            reportVerticalNav(null);
        };

        fetchNavConfig();

        return () => {
            cancelled = true;
        };
    }, [userContext?.activeContext?.tenantId, userContext?.serviceLine?.name]);

    const handleViewsLoaded = (loadedViews: ViewType[]) => {
        setViews(loadedViews);
    };

    const handleActiveViewChange = (view: ViewType) => {
        setActiveView(view);
    };

    const handleViewChange = (view: ViewType) => {
        reportNavViewSet(null);
        if (tableRef.current?.handleViewChange) {
            tableRef.current.handleViewChange(view);
        }
    };

    const handleManageViewsClick = (event: React.MouseEvent<HTMLElement>) => {
        setManageViewsAnchorEl(event.currentTarget);
    };

    const handleCloseManageViews = () => {
        setManageViewsAnchorEl(null);
    };

    const handleRefreshViews = async () => {
        if (tableRef.current?.refreshViews) {
            await tableRef.current.refreshViews();
        }
    };

    // activeView carries the heading for both nav sources: handleNavViewSetSelect names its virtual
    // view after the view set's label, and a saved or queue view supplies its own name. trim() is
    // load-bearing — isValidNavViewSetConfig validates `label` only as a non-empty string, so a
    // whitespace label would otherwise render a blank heading.
    const navTitle = activeView?.name?.trim();

    // The table reports its selected nav view set only after its views, column registry and
    // nav_section_config have all landed — a full round trip after showNewNav flips, during
    // which the header would show the gear under an already-rendered nav column. Seeding from
    // the table's own persisted selection covers that gap; the table's first report, including
    // the `null` a saved view emits, retires the seed for good.
    const persistedNavViewSetId = useMemo(() => {
        const persisted = readPersistedNavSelection(
            buildNavSelectionStorageKey(
                directoryType,
                userContext?.serviceLine?.name,
                userContext?.activeContext?.tenantId
            )
        );
        return persisted?.kind === "navViewSet" ? persisted.navViewSetId : null;
    }, [directoryType, userContext]);

    const effectiveNavViewSetId = navSelectionReported ? activeNavViewSetId : persistedNavViewSetId;

    const showManagerActions =
        showNewNav && effectiveNavViewSetId === MY_TEAMS_WORK_NAV_VIEW_SET_ID;
    const canPrioritize =
        !!widgetOptions?.canPrioritize &&
        typeof widgetOptions?.onOpenPrioritizeWork === "function";

    if (!directoryType) return <></>;

    return (
        <DefaultWrapper fillAvailableHeight={showNewNav}>
            <Box sx={{
                display: "flex",
                flex: 1,
                minHeight: 0,
                width: "100%",
                margin: 0,
                marginLeft: 0,
                padding: 0,
                gap: 0,
                alignItems: "stretch"
            }}>
                {/* Navigation Sidebar - Only show if new nav is enabled */}
                {showNewNav && (
                    <NavigationColumn
                        mode={"worklist"}
                        views={views}
                        activeView={activeView}
                        onViewChange={handleViewChange}
                        roles={roles}
                        onManageViewsClick={handleManageViewsClick}
                        navViewSetConfig={(tableRef.current as any)?.navViewSetConfig ?? DEFAULT_NAV_VIEW_SET_CONFIG.navViewConfig}
                        activeNavViewSetId={activeNavViewSetId}
                        onNavViewSetSelect={(id) => {
                            reportNavViewSet(id);
                            (tableRef.current as any)?.handleNavViewSetSelect(id);
                        }}
                    />
                )}

                {/* Main Content */}
                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    width: "100%",
                    margin: 0,
                    padding: "2.5rem"
                }}>
                    <WorkItemTable
                        DirectoryType={directoryType}
                        widgetHeader={
                            // CDMX-15947 — only on the coder "My Work" view set, render an empty
                            // placeholder here (content column, above the tabs) for dashboard-ui to
                            // portal its "My Work" top section into. Every other view (My Team's
                            // Work, queue views, saved views) keeps the original ProductivityTracker.
                            widgetOptions?.coderViewV2 && activeNavViewSetId === 'my-work'
                            ? <div data-coder-header-slot="true" style={{ width: '100%', flexBasis: '100%' }} />
                            : widgetOptions?.prodTrackerActive
                            ? <ProductivityTrackerHeader loading={loading} productivityNumbers={productivityNumbers}/>
                            : null
                        }
                        widgetType={directoryType}
                        widgetTitle={
                            // showNewNav is load-bearing: activeNavViewSetId is not cleared when
                            // showNewNav, the tenant, or the service line changes, so a team view
                            // set selected while the vertical nav was on can outlive a switch to a
                            // service line where it is off.
                            widgetOptions?.coderViewV2 && activeNavViewSetId === 'my-work'
                                ? undefined
                                : showNewNav && navTitle
                                ? navTitle
                                : "Worklist"
                        }
                        widgetTitleActions={
                            widgetOptions?.coderViewV2 && activeNavViewSetId === 'my-work'
                                ? null
                                : showManagerActions ? (
                                    <ManagerActionButtons
                                        onManageQueues={
                                            hasWorklistSettingsAccess
                                                ? () => setManageQueuesOpen(true)
                                                : undefined
                                        }
                                        onPrioritizeWork={
                                            canPrioritize
                                                ? () => widgetOptions.onOpenPrioritizeWork()
                                                : undefined
                                        }
                                    />
                                ) : !showNewNav && hasWorklistSettingsAccess ? (
                                    <WorkItemSettingsBtn
                                        onClick={() => setQueueSettingsOpen(true)}
                                        alt={"queue settings"}
                                    />
                                ) : null
                        }
                        client={client}
                        handleRowClick={(workitem, data) => {
                            widgetOptions?.onWorkItemSelected(workitem, {
                                ...data,
                                isCodingQuality: false,
                            });
                        }}
                        onTableRefresh={() => setRefresh((prev) => !prev)}
                        childRef={tableRef}
                        handleViewWorkitemData={(workitem) => widgetOptions?.viewWorkitemData(workitem)}
                        hideTabNavigation={showNewNav}
                        useNavHeadingStyle={showNewNav}
                        onViewsLoaded={handleViewsLoaded}
                        onActiveViewChange={handleActiveViewChange}
                        onNavViewSetChange={reportNavViewSet}
                    />
                </Box>
            </Box>

            {/* Manage Views Popup - Only show for new nav */}
            {showNewNav && tableRef.current && (
                <caseContext.Provider
                    value={{
                        directoryType: directoryType,
                        columnOptions: tableRef.current?.getColumnOptions?.() || [],
                        detailGridOptions: tableRef.current?.getDetailGridOptions?.() || [],
                        optionsDict: tableRef.current?.getOptionsDict?.() || [],
                        setOptionsDict: tableRef.current?.setOptionsDict
                    }}
                >
                    <AddViewPopup
                        anchorEl={manageViewsAnchorEl}
                        handleClose={handleCloseManageViews}
                        client={client}
                        handleSaveView={tableRef.current?.handleSaveView || (() => {})}
                        options={tableRef.current?.getColumnOptions?.() || []}
                        onHide={() => {}}
                        onSelect={() => {}}
                        existingView={activeView}
                        views={views}
                        roles={roles}
                        allCoders={tableRef.current?.getAllCoders?.() || []}
                        allGroups={tableRef.current?.getAllGroups?.() || []}
                        queues={tableRef.current?.getQueues?.() || []}
                        directoryType={directoryType}
                        widgetType={directoryType}
                        refreshViews={handleRefreshViews}
                        filters={[]}
                        setFilters={() => {}}
                    />
                </caseContext.Provider>
            )}

            {hasWorklistSettingsAccess && (
                <QueueSettingsDrawer
                    isOpen={queueSettingsOpen}
                    onClose={() => setQueueSettingsOpen(false)}
                    client={props.client}
                    enableQueueViews={showNewNav}
                />
            )}

            {hasWorklistSettingsAccess && (
                <QueueSettingsModal
                    isOpen={manageQueuesOpen}
                    onClose={() => setManageQueuesOpen(false)}
                    client={props.client}
                    enableQueueViews={showNewNav}
                />
            )}
        </DefaultWrapper>
    );
}
